import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { usePatient } from "../../context/PatientContext";
import { useTheme } from "../../context/ThemeContext";
import TransliteratedInput from "../common/TransliteratedInput";
import AutoSave from "../common/AutoSave";

const MandalInfoForm = (props) => {
  const { initialValues, onSubmit, onAutoSave } = props;
  const { doctorInfo } = usePatient();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { theme, isDarkMode } = useTheme();

  const validationSchema = Yup.object().shape({
    MANDALI_FULL_NAME: Yup.string().required("Full name is required"),
    MANDALI_MOB_NO: Yup.string()
      .matches(/^\d{10}$/, "Mobile number must be 10 digits")
      .required("Mobile number is required"),
    MANDALI_PASSWORD: Yup.string()
      .min(8, "Password must be at least 8 characters long")
      .required("Password is required"),
    CONFIRM_PASSWORD: Yup.string()
      .oneOf([Yup.ref("MANDALI_PASSWORD"), null], "Passwords must match")
      .required("Confirm password is required"),
    MANDALI_ROLE: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : Number(value)))
      .required("Role is required")
      .oneOf([1, 2, 3], "Invalid role selected"),
  });

  return (
    <Formik
      initialValues={{
        ...initialValues,
        MANDALI_PASSWORD: "",
        CONFIRM_PASSWORD: ""
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, actions) => {
        onSubmit(values);
        actions.setSubmitting(false);
      }}
      enableReinitialize={true}
    >
      {({ errors, touched, isSubmitting, values }) => (
        <Form className="space-y-6 animate-fadeIn">
          <AutoSave onAutoSave={() => (onAutoSave || onSubmit)?.(values, { isAutoSave: true })} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                htmlFor="MANDALI_FULL_NAME"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Full Name <span className="text-red-500">*</span>
              </label>
              <Field name="MANDALI_FULL_NAME">
                {({ field, form }) => (
                  <TransliteratedInput
                    {...field}
                    onChangeText={(text) => form.setFieldValue(field.name, text)}
                    className={`w-full px-4 py-2.5 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.MANDALI_FULL_NAME && errors.MANDALI_FULL_NAME
                      ? 'border-red-500 focus:ring-red-500'
                      : 'focus:ring-opacity-50'
                      } ${doctorInfo.MANDALI_ROLE !== 1 ? 'opacity-60 cursor-not-allowed' : ''}`}
                    style={{
                      backgroundColor: theme.inputBg,
                      borderColor: touched.MANDALI_FULL_NAME && errors.MANDALI_FULL_NAME ? '#d63031' : theme.border,
                      color: theme.text,
                    }}
                    disabled={doctorInfo.MANDALI_ROLE !== 1}
                    placeholder="Enter full name"
                  />
                )}
              </Field>
              <ErrorMessage name="MANDALI_FULL_NAME" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="MANDALI_MOB_NO"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <Field
                name="MANDALI_MOB_NO"
                type="text"
                disabled={doctorInfo.MANDALI_ROLE !== 1}
                className={`w-full px-4 py-2.5 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.MANDALI_MOB_NO && errors.MANDALI_MOB_NO
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  } ${doctorInfo.MANDALI_ROLE !== 1 ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.MANDALI_MOB_NO && errors.MANDALI_MOB_NO ? '#d63031' : theme.border,
                  color: theme.text,
                }}
                placeholder="Enter 10-digit mobile number"
              />
              <ErrorMessage name="MANDALI_MOB_NO" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="MANDALI_PASSWORD"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Field
                  name="MANDALI_PASSWORD"
                  type={showPassword ? "text" : "password"}
                  className={`w-full px-4 py-2.5 pr-12 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.MANDALI_PASSWORD && errors.MANDALI_PASSWORD
                    ? "border-red-500 focus:ring-red-500"
                    : "focus:ring-opacity-50"
                    }`}
                  style={{
                    backgroundColor: theme.inputBg,
                    borderColor: touched.MANDALI_PASSWORD && errors.MANDALI_PASSWORD ? '#d63031' : theme.border,
                    color: theme.text,
                  }}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-lg hover:scale-110 transition-transform"
                  style={{ color: theme.textSecondary }}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
              <ErrorMessage name="MANDALI_PASSWORD" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="CONFIRM_PASSWORD"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Field
                  name="CONFIRM_PASSWORD"
                  type={showConfirmPassword ? "text" : "password"}
                  className={`w-full px-4 py-2.5 pr-12 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.CONFIRM_PASSWORD && errors.CONFIRM_PASSWORD
                    ? "border-red-500 focus:ring-red-500"
                    : "focus:ring-opacity-50"
                    }`}
                  style={{
                    backgroundColor: theme.inputBg,
                    borderColor: touched.CONFIRM_PASSWORD && errors.CONFIRM_PASSWORD ? '#d63031' : theme.border,
                    color: theme.text,
                  }}
                  placeholder="Repeat new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-lg hover:scale-110 transition-transform"
                  style={{ color: theme.textSecondary }}
                >
                  {showConfirmPassword ? "🙈" : "👁️"}
                </button>
              </div>
              <ErrorMessage name="CONFIRM_PASSWORD" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label
                htmlFor="MANDALI_ROLE"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Role <span className="text-red-500">*</span>
              </label>
              <Field
                as="select"
                name="MANDALI_ROLE"
                disabled={doctorInfo.MANDALI_ROLE !== 1}
                className={`w-full px-4 py-2.5 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.MANDALI_ROLE && errors.MANDALI_ROLE
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  } ${doctorInfo.MANDALI_ROLE !== 1 ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.MANDALI_ROLE && errors.MANDALI_ROLE ? '#d63031' : theme.border,
                  color: theme.text,
                }}
              >
                <option value="">Select Role</option>
                <option value="1">Admin</option>
                <option value="2">Doctor</option>
                <option value="3">Attendant</option>
              </Field>
              <ErrorMessage name="MANDALI_ROLE" component="div" className="text-red-500 text-sm mt-1" />
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 md:gap-4 pt-6 border-t" style={{ borderColor: theme.border }}>
            <button
              onClick={() => {
                if (doctorInfo?.MANDALI_ROLE === 1) {
                  navigate("/doctorslist");
                } else {
                  navigate("/getmandaliinfo");
                }
              }}
              type="button"
              className="px-6 py-2.5 rounded-full font-semibold transition-all duration-200 hover:scale-105 active:scale-95 text-xs md:text-sm"
              style={{
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                color: theme.text,
                border: `1px solid ${theme.border}`
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 md:px-8 py-2.5 rounded-full font-bold text-white transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-base shadow-lg"
              style={{
                backgroundImage: `linear-gradient(to right, ${theme.brownTwo}, ${theme.secondary}, ${theme.primary}, ${theme.brownTwo})`,
                backgroundSize: '200% auto',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundPosition = 'right center'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundPosition = 'left center'}
            >
              Update Information
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default MandalInfoForm;
